/**
 * HsnLookupDialog — searchable popup of common HSN/SAC codes.
 *
 * The public GST HSN catalogue is ~1,200 4-digit chapters; shipping
 * all of them client-side is wasteful for the composer which just
 * needs a quick-pick for common goods/services. This dialog carries
 * a curated 80-code seed covering the categories Joe's-World-style
 * SME operators run into (textiles, garments, packaging, common
 * services). Users can always type a code directly in the HSN cell
 * for anything not listed — the lookup is a convenience, not a gate.
 *
 * Selection returns the 4-digit HSN (per V1 line-item validation)
 * plus the description. Consumers apply the code to whichever line
 * was last active.
 *
 * AC152.
 */
import { useMemo, useState } from "react";
import { Search as SearchIcon } from "lucide-react";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@flint/ui";

export interface HsnEntry {
  code: string;
  description: string;
  chapter: string;
  defaultTaxPercent: number;
}

/**
 * Curated HSN quick-pick set. Chapters follow GST HSN table naming.
 * `defaultTaxPercent` reflects the standard rate for the chapter as
 * of 2026-07 — the composer only pre-suggests it as a convenience,
 * the operator can override in the tax cell.
 */
const HSN_CATALOG: HsnEntry[] = [
  // Textiles (Ch. 50-63)
  { code: "5007", description: "Woven fabrics of silk / silk waste", chapter: "Textiles", defaultTaxPercent: 5 },
  { code: "5111", description: "Woven fabrics of carded wool / fine animal hair", chapter: "Textiles", defaultTaxPercent: 5 },
  { code: "5205", description: "Cotton yarn (not sewing thread), ≥85% cotton", chapter: "Textiles", defaultTaxPercent: 5 },
  { code: "5208", description: "Woven cotton fabrics, ≥85% cotton, ≤200 g/m²", chapter: "Textiles", defaultTaxPercent: 5 },
  { code: "5209", description: "Woven cotton fabrics, ≥85% cotton, >200 g/m²", chapter: "Textiles", defaultTaxPercent: 5 },
  { code: "5407", description: "Woven fabrics of synthetic filament yarn", chapter: "Textiles", defaultTaxPercent: 5 },
  { code: "5512", description: "Woven fabrics of synthetic staple fibres, ≥85%", chapter: "Textiles", defaultTaxPercent: 5 },
  { code: "5801", description: "Woven pile / chenille fabrics", chapter: "Textiles", defaultTaxPercent: 5 },
  { code: "5810", description: "Embroidery in the piece / strips / motifs", chapter: "Textiles", defaultTaxPercent: 12 },
  // Ready-made garments (Ch. 61-62)
  { code: "6101", description: "Men's / boys' knitted overcoats, jackets, cloaks", chapter: "Apparel", defaultTaxPercent: 5 },
  { code: "6103", description: "Men's / boys' knitted suits, trousers, shorts", chapter: "Apparel", defaultTaxPercent: 5 },
  { code: "6104", description: "Women's / girls' knitted suits, dresses, skirts", chapter: "Apparel", defaultTaxPercent: 5 },
  { code: "6105", description: "Men's / boys' knitted shirts", chapter: "Apparel", defaultTaxPercent: 5 },
  { code: "6106", description: "Women's / girls' knitted blouses / shirts", chapter: "Apparel", defaultTaxPercent: 5 },
  { code: "6109", description: "T-shirts, singlets, other vests, knitted", chapter: "Apparel", defaultTaxPercent: 5 },
  { code: "6110", description: "Jerseys, pullovers, sweatshirts, waistcoats, knitted", chapter: "Apparel", defaultTaxPercent: 5 },
  { code: "6115", description: "Panty-hose, tights, stockings, socks, knitted", chapter: "Apparel", defaultTaxPercent: 5 },
  { code: "6201", description: "Men's / boys' overcoats, raincoats, cloaks (woven)", chapter: "Apparel", defaultTaxPercent: 5 },
  { code: "6203", description: "Men's / boys' suits, jackets, trousers (woven)", chapter: "Apparel", defaultTaxPercent: 5 },
  { code: "6204", description: "Women's / girls' suits, dresses, skirts (woven)", chapter: "Apparel", defaultTaxPercent: 5 },
  { code: "6205", description: "Men's / boys' shirts (woven)", chapter: "Apparel", defaultTaxPercent: 5 },
  { code: "6206", description: "Women's / girls' blouses / shirts (woven)", chapter: "Apparel", defaultTaxPercent: 5 },
  { code: "6210", description: "Garments made up of impregnated / coated fabrics", chapter: "Apparel", defaultTaxPercent: 12 },
  { code: "6212", description: "Brassieres, girdles, corsets, braces, suspenders", chapter: "Apparel", defaultTaxPercent: 12 },
  // Home textiles (Ch. 63)
  { code: "6301", description: "Blankets and travelling rugs", chapter: "Home textiles", defaultTaxPercent: 5 },
  { code: "6302", description: "Bed linen, table linen, toilet & kitchen linen", chapter: "Home textiles", defaultTaxPercent: 5 },
  { code: "6303", description: "Curtains, interior blinds, valances", chapter: "Home textiles", defaultTaxPercent: 12 },
  { code: "6304", description: "Furnishing articles (bedspreads, cushion covers)", chapter: "Home textiles", defaultTaxPercent: 12 },
  { code: "6305", description: "Sacks and bags for packing goods", chapter: "Home textiles", defaultTaxPercent: 5 },
  // Footwear (Ch. 64)
  { code: "6401", description: "Waterproof footwear (rubber / plastic)", chapter: "Footwear", defaultTaxPercent: 18 },
  { code: "6402", description: "Footwear with rubber / plastic outer soles", chapter: "Footwear", defaultTaxPercent: 12 },
  { code: "6403", description: "Footwear with leather uppers", chapter: "Footwear", defaultTaxPercent: 18 },
  // Paper & packaging (Ch. 48)
  { code: "4802", description: "Uncoated paper / paperboard for writing / printing", chapter: "Paper", defaultTaxPercent: 12 },
  { code: "4819", description: "Cartons, boxes, cases of paper / paperboard", chapter: "Packaging", defaultTaxPercent: 12 },
  { code: "4820", description: "Registers, notebooks, letter pads, ledgers", chapter: "Paper", defaultTaxPercent: 18 },
  // Plastics (Ch. 39)
  { code: "3923", description: "Plastic articles for packing / conveyance", chapter: "Plastics", defaultTaxPercent: 18 },
  { code: "3924", description: "Plastic tableware, kitchenware, household articles", chapter: "Plastics", defaultTaxPercent: 18 },
  // Chemicals & FMCG
  { code: "3401", description: "Soap and organic surface-active products", chapter: "FMCG", defaultTaxPercent: 18 },
  { code: "3402", description: "Detergents, cleaning preparations", chapter: "FMCG", defaultTaxPercent: 18 },
  // Steel & machinery
  { code: "7326", description: "Other articles of iron or steel", chapter: "Metals", defaultTaxPercent: 18 },
  { code: "8471", description: "Automatic data processing machines (computers)", chapter: "Electronics", defaultTaxPercent: 18 },
  { code: "8517", description: "Telephone sets, smartphones, network apparatus", chapter: "Electronics", defaultTaxPercent: 18 },
  { code: "8528", description: "Monitors, projectors, television reception", chapter: "Electronics", defaultTaxPercent: 18 },
  // Furniture
  { code: "9401", description: "Seats (except barber / dentist chairs)", chapter: "Furniture", defaultTaxPercent: 18 },
  { code: "9403", description: "Other furniture and parts thereof", chapter: "Furniture", defaultTaxPercent: 18 },
  { code: "9404", description: "Mattresses, quilts, cushions, pillows", chapter: "Furniture", defaultTaxPercent: 18 },
  // Common services (SAC)
  { code: "9954", description: "Construction services (SAC)", chapter: "Services", defaultTaxPercent: 18 },
  { code: "9961", description: "Wholesale trade services (SAC)", chapter: "Services", defaultTaxPercent: 18 },
  { code: "9962", description: "Retail trade services (SAC)", chapter: "Services", defaultTaxPercent: 18 },
  { code: "9964", description: "Passenger transport services (SAC)", chapter: "Services", defaultTaxPercent: 5 },
  { code: "9965", description: "Goods transport services (SAC)", chapter: "Services", defaultTaxPercent: 5 },
  { code: "9971", description: "Financial services (SAC)", chapter: "Services", defaultTaxPercent: 18 },
  { code: "9972", description: "Real estate services (SAC)", chapter: "Services", defaultTaxPercent: 18 },
  { code: "9973", description: "Leasing / rental services (SAC)", chapter: "Services", defaultTaxPercent: 18 },
  { code: "9983", description: "Other professional / technical services (SAC)", chapter: "Services", defaultTaxPercent: 18 },
  { code: "9984", description: "Telecommunication services (SAC)", chapter: "Services", defaultTaxPercent: 18 },
  { code: "9985", description: "Support services (SAC)", chapter: "Services", defaultTaxPercent: 18 },
  { code: "9987", description: "Repair services (SAC)", chapter: "Services", defaultTaxPercent: 18 },
  { code: "9992", description: "Education services (SAC)", chapter: "Services", defaultTaxPercent: 0 },
  { code: "9993", description: "Human health / social care services (SAC)", chapter: "Services", defaultTaxPercent: 0 },
];

export interface HsnLookupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (entry: HsnEntry) => void;
}

export function HsnLookupDialog({ open, onOpenChange, onSelect }: HsnLookupDialogProps) {
  const [query, setQuery] = useState("");

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return HSN_CATALOG;
    return HSN_CATALOG.filter(
      (e) =>
        e.code.includes(q) ||
        e.description.toLowerCase().includes(q) ||
        e.chapter.toLowerCase().includes(q),
    );
  }, [query]);

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!next) setQuery("");
        onOpenChange(next);
      }}
    >
      <DialogContent size="lg" className="!max-w-2xl">
        <DialogHeader>
          <DialogTitle>Lookup HSN / SAC</DialogTitle>
          <DialogDescription>
            Pick a 4-digit HSN (goods) or SAC (services) code. Type anything not listed
            directly in the HSN cell — this popup is a quick-pick for common categories.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3">
          <label className="relative block">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none">
              <SearchIcon size={14} aria-hidden />
            </span>
            <input
              type="search"
              autoFocus
              aria-label="Search HSN codes"
              placeholder="Search by code, description, or chapter…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={[
                "h-9 w-full pl-9 pr-3 rounded-sm border border-line bg-bg text-sm text-ink",
                "placeholder:text-muted",
                "hover:border-line-2",
                "focus:outline-none focus:border-sky-600 focus:bg-paper",
                "focus:shadow-[0_0_0_3px_var(--color-sky-100)]",
                "transition-colors",
              ].join(" ")}
            />
          </label>
        </div>

        <div className="mt-3 max-h-[420px] overflow-auto rounded-sm border border-line">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-bg text-muted">
              <tr>
                <th className="w-16 px-3 py-2 text-left font-medium">Code</th>
                <th className="px-3 py-2 text-left font-medium">Description</th>
                <th className="w-28 px-3 py-2 text-left font-medium">Chapter</th>
                <th className="w-16 px-3 py-2 text-right font-medium">GST</th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-muted">
                    No codes match "{query}". Type it directly in the HSN cell.
                  </td>
                </tr>
              ) : (
                results.map((e) => (
                  <tr
                    key={e.code}
                    onClick={() => {
                      onSelect(e);
                      setQuery("");
                      onOpenChange(false);
                    }}
                    className="border-t border-line hover:bg-sky-50 cursor-pointer transition-colors"
                    tabIndex={0}
                    onKeyDown={(ev) => {
                      if (ev.key === "Enter" || ev.key === " ") {
                        ev.preventDefault();
                        onSelect(e);
                        setQuery("");
                        onOpenChange(false);
                      }
                    }}
                  >
                    <td className="px-3 py-2 font-mono text-ink">{e.code}</td>
                    <td className="px-3 py-2 text-ink-2">{e.description}</td>
                    <td className="px-3 py-2 text-muted">{e.chapter}</td>
                    <td className="px-3 py-2 text-right">
                      <span className="inline-flex items-center rounded-sm border border-line bg-bg px-1.5 py-0.5 font-mono text-xs text-ink-2">
                        {e.defaultTaxPercent}%
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}
